import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Clock, Users, AlertCircle, Loader2, Briefcase, CheckCircle2 } from 'lucide-react';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import JobDetailsModal from '../components/dashboard/JobDetailsModal';
import { jobsApi } from '../api/jobs';
import { cn } from '../utils/cn';
import { useLanguage } from '../contexts/LanguageContext';

interface CalendarJob {
  id: string;
  title: string;
  department: string;
  location: string;
  candidates: number;
  referencesNeeded: number;
  referencesCompleted: number;
  status: 'active' | 'completed' | 'ACTIVE' | 'COMPLETED';
  priority: 'high' | 'low';
  dueDate: string;
  daysUntilDue: number;
}

const Calendar = () => {
  const { t } = useLanguage();
  const [selectedJob, setSelectedJob] = useState<CalendarJob | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [jobs, setJobs] = useState<CalendarJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [eventPage, setEventPage] = useState(0);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);
        const jobsData = await jobsApi.getAll();
        
        // Transform API data to match expected format
        const transformedJobs = await Promise.all(jobsData.map(async job => {
          // Calculate days until deadline
          const dueDate = new Date(job.application_deadline);
          const today = new Date();
          const daysUntilDue = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
          
          // Determine priority based on days until deadline
          let priority: 'high' | 'low';
          
          // Calculate priority based on new due date thresholds:
          // High (yellow): <= 3 days
          // Low (green): > 3 days
          if (daysUntilDue <= 3) {
            priority = 'high';
          } else {
            priority = 'low';
          }

          // Get candidates for this job
          const candidates = await jobsApi.getCandidates(job.id);
          
          // Calculate references completed
          const referencesCompleted = candidates.reduce((total, candidate) => {
            return total + (candidate.references?.filter(ref => ref.filled_form_at)?.length || 0);
          }, 0);

          return {
            id: job.id,
            title: job.job_title,
            department: job.department,
            location: job.location || 'Remote',
            candidates: candidates.length,
            referencesNeeded: job.required_number_of_references * candidates.length,
            referencesCompleted,
            status: job.status || 'active',
            priority,
            dueDate: job.application_deadline,
            daysUntilDue
          };
        }));

        setJobs(transformedJobs);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch jobs:', err);
        setError(t('calendar.error.message'));
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  const getUpcomingEvents = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    const filteredJobs = jobs
      .filter(job => {
        // Only include active jobs with future due dates (exclude completed jobs)
        const isActive = job.status !== 'COMPLETED' && job.status !== 'completed';
        const dueDate = new Date(job.dueDate);
        dueDate.setHours(0, 0, 0, 0); // Reset time to start of day
        const isFuture = dueDate >= today;
        return isActive && isFuture;
      })
      .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
    
    // Calculate start index for pagination (3 items per page)
    const startIndex = eventPage * 3;
    return filteredJobs.slice(startIndex, startIndex + 3);
  };
  
  const getAllUpcomingEvents = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    return jobs
      .filter(job => {
        const isActive = job.status !== 'COMPLETED' && job.status !== 'completed';
        const dueDate = new Date(job.dueDate);
        dueDate.setHours(0, 0, 0, 0); // Reset time to start of day
        const isFuture = dueDate >= today;
        return isActive && isFuture;
      })
      .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
  };
  
  const handleNextEvents = () => {
    const allEvents = getAllUpcomingEvents();
    const maxPage = Math.ceil(allEvents.length / 3) - 1;
    if (eventPage < maxPage) {
      setEventPage(prev => prev + 1);
    }
  };
  
  const handlePrevEvents = () => {
    if (eventPage > 0) {
      setEventPage(prev => prev - 1);
    }
  };

  const formatDueDate = (date: string) => {
    const dueDate = new Date(date);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (dueDate.toDateString() === today.toDateString()) {
      return t('calendar.upcoming.today');
    } else if (dueDate.toDateString() === tomorrow.toDateString()) {
      return t('calendar.upcoming.tomorrow');
    } else {
      return dueDate.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getJobsForDate = (date: Date) => {
    return jobs.filter(job => {
      const dueDate = new Date(job.dueDate);
      return dueDate.getDate() === date.getDate() &&
             dueDate.getMonth() === date.getMonth() &&
             dueDate.getFullYear() === date.getFullYear();
    });
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDayOfMonth = getFirstDayOfMonth(currentDate);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="h-28 border-t border-r bg-gray-50" />);
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const jobsForDate = getJobsForDate(date);
      const isToday = date.toDateString() === new Date().toDateString();
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;

      days.push(
        <div 
          key={day} 
          className={cn(
            "h-28 border-t border-r p-2 relative transition-all",
            isToday && "bg-blue-50 shadow-inner",
            isWeekend && !isToday && "bg-gray-50",
            jobsForDate.length > 0 && "hover:shadow-inner"
          )}
        >
          <div className={cn(
            "font-medium text-sm mb-2 flex items-center justify-center w-6 h-6 rounded-full",
            isToday && "bg-blue-600 text-white"
          )}>
            {day}
          </div>
          <div className="overflow-auto max-h-[calc(100%-24px)]">
            {jobsForDate.map(job => {
              const isCompleted = job.status === 'COMPLETED' || job.status === 'completed';
              return (
                <button
                  key={job.id}
                  onClick={() => setSelectedJob(job)}
                  className={cn(
                    "w-full text-left text-xs p-1.5 rounded mb-1 truncate transition-colors flex items-center bg-gray-50 hover:bg-gray-100",
                    isCompleted 
                      ? 'text-purple-700'
                      : job.priority === 'high' 
                        ? 'text-yellow-700' 
                        : 'text-green-700'
                  )}
                >
                  <span className="w-2 h-2 rounded-full mr-1.5 flex-shrink-0" style={{
                    backgroundColor: isCompleted ? '#9333ea' : 
                      job.priority === 'high' ? '#eab308' : '#22c55e'
                  }}></span>
                  {job.title}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    return days;
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col">
          <Header />
          <main className="flex-1 p-4 lg:p-6">
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-3 text-gray-500">
                <Loader2 className="w-10 h-10 animate-spin" />
                <span>{t('calendar.loading')}</span>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col">
          <Header />
          <main className="flex-1 p-4 lg:p-6">
            <div className="bg-red-50 text-red-600 p-6 rounded-lg">
              <h3 className="font-medium text-lg mb-2">{t('calendar.error.title')}</h3>
              <p>{error}</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      
      <div className="flex-1 flex flex-col">
        <Header />
        
        <main className="flex-1 p-4 lg:p-6">
          {/* Calendar Header Section */}
          <div className="bg-white rounded-xl shadow-sm border p-4 lg:p-6 mb-1">
            <div className="mb-0">
              <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center">
                <CalendarIcon className="w-6 h-6 text-blue-600 mr-2" />
                {t('calendar.title')}
              </h2>
              <p className="text-gray-500 text-sm tracking-wide">{t('calendar.subtitle')}</p>
            </div>
          </div>

          {/* Next Events Section */}
          <div className="mb-5 bg-white rounded-xl shadow-sm border p-4 lg:p-5">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900">{t('calendar.upcoming.title')}</h2>
            </div>
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {getUpcomingEvents().map((event) => (
                  <button
                    key={event.id}
                    onClick={() => setSelectedJob(event)}
                    className="bg-gray-50 p-4 rounded-lg border hover:shadow-md transition-shadow text-left relative overflow-hidden"
                  >
                    {/* Priority indicator */}
                    <div className={cn(
                      "absolute top-0 left-0 w-1 h-full",
                      event.priority === 'high' ? 'bg-yellow-500' :
                      'bg-green-500'
                    )} />
                    
                    <div className="flex items-start gap-3 pl-2">
                      <div className={cn(
                        "w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0",
                        (event.status === 'COMPLETED' || event.status === 'completed')
                          ? 'bg-green-50'
                          : event.priority === 'high' 
                            ? 'bg-yellow-50' 
                            : 'bg-green-50'
                      )}>
                        {(event.status === 'COMPLETED' || event.status === 'completed')
                          ? <CheckCircle2 className="w-4 h-4 text-green-600" />
                          : <Clock className={cn(
                              "w-4 h-4",
                              event.priority === 'high' ? 'text-yellow-600' :
                              'text-green-600'
                            )} />
                        }
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{event.title}</div>
                        <div className="text-sm text-gray-500 mt-1">{event.department}</div>
                        <div className="flex items-center gap-1 mt-2 text-sm">
                          <span className={cn(
                            "font-medium",
                            event.priority === 'high' ? 'text-yellow-600' :
                            'text-green-600'
                          )}>
                            {formatDueDate(event.dueDate)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              
              {/* Navigation controls */}
              <div className="flex items-center justify-center gap-4 pt-1">
                <button 
                  onClick={handlePrevEvents}
                  disabled={eventPage === 0}
                  className={cn(
                    "flex items-center justify-center p-2 rounded-lg",
                    eventPage === 0 
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed" 
                      : "bg-white text-blue-600 hover:bg-blue-50 border border-blue-200"
                  )}
                  aria-label="Previous jobs"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                
                <div className="text-sm text-gray-500">
                  {eventPage + 1} / {Math.max(1, Math.ceil(getAllUpcomingEvents().length / 3))}
                </div>
                
                <button 
                  onClick={handleNextEvents}
                  disabled={getAllUpcomingEvents().length <= (eventPage + 1) * 3}
                  className={cn(
                    "flex items-center justify-center p-2 rounded-lg",
                    getAllUpcomingEvents().length <= (eventPage + 1) * 3 
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed" 
                      : "bg-white text-blue-600 hover:bg-blue-50 border border-blue-200"
                  )}
                  aria-label="Next jobs"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Calendar Section */}
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="p-4 lg:p-5 border-b bg-gray-50 flex items-center justify-between">
              <div className="flex items-center">
                <CalendarIcon className="w-5 h-5 text-blue-600 mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">
                  {currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={prevMonth}
                  className="p-2 rounded-md transition-all flex items-center justify-center bg-white text-blue-600 hover:bg-blue-50 border border-blue-200 hover:border-blue-300"
                  aria-label="Previous month"
                  title="Previous month"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={nextMonth}
                  className="p-2 rounded-md transition-all flex items-center justify-center bg-white text-blue-600 hover:bg-blue-50 border border-blue-200 hover:border-blue-300"
                  aria-label="Next month"
                  title="Next month"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-7 border-b bg-blue-50">
              {[
                t('calendar.days.sun'), 
                t('calendar.days.mon'), 
                t('calendar.days.tue'), 
                t('calendar.days.wed'), 
                t('calendar.days.thu'), 
                t('calendar.days.fri'), 
                t('calendar.days.sat')
              ].map((day, index) => (
                <div 
                  key={day} 
                  className={cn(
                    "py-2.5 text-center text-xs font-semibold",
                    index === 0 || index === 6 ? "text-blue-600" : "text-gray-700"
                  )}
                >
                  {day}
                </div>
              ))}
            </div>
            
            <div className="grid grid-cols-7">
              {renderCalendar()}
            </div>
          </div>
        </main>
      </div>

      {selectedJob && (
        <JobDetailsModal 
          job={selectedJob} 
          onClose={() => setSelectedJob(null)} 
        />
      )}
    </div>
  );
};

export default Calendar;